import { ChevronLeft, ChevronRight, Camera } from "lucide-react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { BottomNavigationBarProps } from "./types";

const buttonClass = (disabled: boolean) =>
  clsx(
    "inline-flex h-9 w-9 items-center justify-center rounded-full transition",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900/20",
    "active:scale-95",
    disabled
      ? "opacity-40 cursor-not-allowed"
      : "text-gray-700 hover:bg-gray-100",
  );

export const BottomNavigationBar = ({
  frameCount,
  currentFrameIndex,
  changeSelectedFrameIndex,
}: BottomNavigationBarProps) => {
  const { t } = useTranslation();
  const isLastFrame = currentFrameIndex === frameCount - 1;

  const onPreviousButtonClick = () => {
    changeSelectedFrameIndex({
      newIndex: Math.max(0, currentFrameIndex - 1),
      method: "overlay-prev",
    });
  };

  const onNextButtonClick = () => {
    changeSelectedFrameIndex({
      newIndex: Math.min(frameCount - 1, currentFrameIndex + 1),
      method: "overlay-next",
    });
  };

  if (frameCount <= 0) return null;

  return (
    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20">
      <div className="rounded-full bg-white/90 backdrop-blur shadow-lg ring-1 ring-black/5 px-3 py-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onPreviousButtonClick}
            disabled={currentFrameIndex === 0}
            aria-label={t("doc_viewer.previous_frame")}
            title={t("doc_viewer.previous_frame")}
            className={buttonClass(currentFrameIndex === 0)}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2 px-2">
            <Camera className="h-5 w-5 text-gray-500" />
            <div className="text-sm font-semibold tabular-nums text-gray-900">
              {currentFrameIndex + 1}
              <span className="mx-1 text-gray-400">/</span>
              <span className="text-gray-600">{frameCount}</span>
            </div>
          </div>
          <div className="mx-1 h-6 w-px bg-gray-200" />
          <button
            type="button"
            onClick={onNextButtonClick}
            disabled={isLastFrame}
            aria-label={t("doc_viewer.next_frame")}
            title={t("doc_viewer.next_frame")}
            className={buttonClass(isLastFrame)}
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>
      <div
        className={clsx(
          "mt-2 text-center text-xs",
          "text-white/90 drop-shadow-sm",
          "hidden sm:block", // optional: avoid clutter on mobile
        )}
      >
        {t("doc_viewer.frame_nav_tip")}
      </div>
    </div>
  );
};
